import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

/**
 * Asking to upload a receipt against one direct payment order.
 *
 * It is {@link PresignUploadRequestSchema} **minus the kind**, and that omission
 * is the point rather than a convenience: this route always presigns
 * `payment_evidence`, and a client able to name the kind would be a client able
 * to mint an upload slot for an `id_document` through a billing permission. The
 * route knows what it is for; the caller only declares what it intends to send.
 *
 * The type and size are still the caller's to declare, for the reason the media
 * contract gives: the signature covers `Content-Type` and `Content-Length`, so a
 * client that lies is refused by storage itself before a byte is written.
 */
export const OrderEvidencePresignSchema = z
  .object({
    mime: z.string().min(1).max(255),
    sizeBytes: z.number().int().positive(),
  })
  .strict();

export type OrderEvidencePresignRequest = z.infer<typeof OrderEvidencePresignSchema>;

/**
 * Finalizing the upload and binding it to the order, in one call.
 *
 * Deliberately **not** two — a finalize followed by a separate attach leaves a
 * window in which a `ready` receipt belongs to nothing, and a client that dies in
 * that window leaves the purge worker to guess. The server finalizes, re-checks
 * the asset's kind, and claims the order's empty `evidence_media_id` in a single
 * act.
 *
 * `mediaId` echoes what the presign minted rather than being chosen: the asset is
 * re-read server-side, and an id naming an asset of any other kind is refused
 * (`orderEvidenceKindInvalid`).
 */
export const OrderEvidenceAttachSchema = z
  .object({
    mediaId: UuidSchema,
  })
  .strict();

export type OrderEvidenceAttachRequest = z.infer<typeof OrderEvidenceAttachSchema>;
